import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { EditorLayout, EditorSidebarSection, EditorSidebarCard, EditorSectionCard, EditorSectionItem, EditorActionCard } from '@/components/editor';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, Bed, Users, Clock, Eye, Globe, GlobeLock, Calendar as CalendarIcon, CreditCard, Home, CalendarOff, FileText, ImageIcon } from 'lucide-react';
import { StickyBottomBar } from '@/components/ui/StickyBottomBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { sv } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import { MediaImageManager } from '@/components/media/MediaImageManager';
import { AccommodationPricingSection } from '@/components/producer/AccommodationPricingSection';
import { AccommodationPagePreview } from '@/components/accommodation/AccommodationPagePreview';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import { useGalleryAssets } from '@/hooks/useGalleryAssets';
import { Card, CardContent } from '@/components/ui/card';
import type { HeroImage } from '@/components/place/HeroCarousel';
import {
  useAccommodation,
  useUpsertAccommodation,
  useAccommodationAvailabilityBlocks,
  useAccommodationBookings,
  useAddManualBlock,
  useDeleteManualBlock,
} from '@/hooks/useProducerAccommodations';
import {
  getBasicInfoSummary,
  getBookingSettingsSummary,
  getPrepaymentSettingsSummary,
  getGuestInfoSummary,
  getAvailabilitySummary,
} from '@/lib/accommodation/editorSummaries';

type PrepaymentPolicy = 'none' | 'optional' | 'required';
type PrepaymentType = 'fixed' | 'percentage';

interface FormState {
  title: string;
  slug: string;
  description_short: string;
  description: string;
  status: string;
  beds: string;
  guest_capacity: string;
  price_per_night_text: string;
  booking_mode: string;
  booking_deadline_hours: string;
  check_in_time: string;
  check_out_time: string;
  house_rules: string;
  prepayment_policy_default: PrepaymentPolicy;
  prepayment_type: PrepaymentType;
  prepayment_amount_default_kr: string;
  prepayment_percentage: string;
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function formatPriceLabel(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/kr|per|natt/i.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
  return trimmed;
}

/* ── Main page ── */
export default function AccommodationEditorPage() {
  const { farmId, accommodationId } = useParams<{ farmId: string; accommodationId: string }>();
  const navigate = useNavigate();
  const isNew = !accommodationId;

  const { data: existing, isLoading } = useAccommodation(isNew ? undefined : accommodationId);
  const { data: blocks } = useAccommodationAvailabilityBlocks(isNew ? undefined : accommodationId);
  const { data: bookings } = useAccommodationBookings(isNew ? undefined : accommodationId);
  const upsert = useUpsertAccommodation();
  const addBlock = useAddManualBlock();
  const deleteBlock = useDeleteManualBlock();

  const effectiveMediaId = isNew ? undefined : accommodationId;
  const { url640: coverUrl640, url1280: coverUrl1280, url320: coverUrl320 } = useCoverAsset('accommodation', effectiveMediaId);
  const { urls320: galleryThumbs, urls640: galleryFull } = useGalleryAssets('accommodation', effectiveMediaId);

  const effectiveOwnerId = isNew ? null : accommodationId ?? null;
  const draftSessionId = isNew ? (accommodationId ?? 'new-accommodation') : '';

  const coverUpload = useMediaUpload({
    ownerType: 'accommodation',
    ownerId: effectiveOwnerId,
    draftSessionId: !effectiveOwnerId ? draftSessionId : undefined,
    kind: 'cover',
    maxFiles: 1,
  });

  const galleryUpload = useMediaUpload({
    ownerType: 'accommodation',
    ownerId: effectiveOwnerId,
    draftSessionId: !effectiveOwnerId ? draftSessionId : undefined,
    kind: 'gallery',
    maxFiles: 10,
  });

  const [form, setForm] = useState<FormState>({
    title: '', slug: '', description_short: '', description: '',
    status: 'draft', beds: '', guest_capacity: '2',
    price_per_night_text: '', booking_mode: 'required',
    booking_deadline_hours: '24', check_in_time: '15:00',
    check_out_time: '11:00', house_rules: '',
    prepayment_policy_default: 'none',
    prepayment_type: 'percentage',
    prepayment_amount_default_kr: '',
    prepayment_percentage: '60',
  });

  const [blockRange, setBlockRange] = useState<DateRange | undefined>();
  const [blockCalendarOpen, setBlockCalendarOpen] = useState(false);
  const [blockNote, setBlockNote] = useState('');
  const [slugManual, setSlugManual] = useState(false);

  // Build hero images array from cover + gallery
  const heroImages = useMemo<HeroImage[]>(() => {
    const imgs: HeroImage[] = [];
    const heroSrc = coverUrl1280 || coverUrl640;
    if (heroSrc) imgs.push({ id: 'cover', src: heroSrc, alt: form.title || 'Boende' });
    galleryFull.forEach((url, i) => imgs.push({ id: `gallery-${i}`, src: url, alt: `${form.title || 'Boende'} bild ${i + 2}` }));
    return imgs;
  }, [coverUrl1280, coverUrl640, galleryFull, form.title]);

  const galleryStripThumbs = useMemo(() => {
    return [coverUrl320 || '', ...galleryThumbs].filter(Boolean);
  }, [coverUrl320, galleryThumbs]);

  const [heroIndex, setHeroIndex] = useState(0);

  const initialSnapshot = useRef<FormState | null>(null);

  useEffect(() => {
    if (existing) {
      const loaded: FormState = {
        title: existing.title || '',
        slug: existing.slug || '',
        description_short: existing.description_short || '',
        description: existing.description || '',
        status: existing.status || 'draft',
        beds: existing.beds?.toString() || '',
        guest_capacity: existing.guest_capacity?.toString() || '2',
        price_per_night_text: existing.price_per_night_text || '',
        booking_mode: existing.booking_mode || 'required',
        booking_deadline_hours: existing.booking_deadline_hours?.toString() || '24',
        check_in_time: existing.check_in_time || '15:00',
        check_out_time: existing.check_out_time || '11:00',
        house_rules: existing.house_rules || '',
        prepayment_policy_default: (existing as any).prepayment_policy_default || 'none',
        prepayment_type: (existing as any).prepayment_type || 'percentage',
        prepayment_amount_default_kr: (existing as any).prepayment_amount_default_ore
          ? Math.round((existing as any).prepayment_amount_default_ore / 100).toString()
          : '',
        prepayment_percentage: (existing as any).prepayment_percentage?.toString() || '60',
      };
      setForm(loaded);
      initialSnapshot.current = loaded;
      setSlugManual(true);
    }
  }, [existing]);

  const isDirty = useMemo(() => {
    if (isNew) return !!form.title.trim();
    if (!initialSnapshot.current) return false;
    return JSON.stringify(form) !== JSON.stringify(initialSnapshot.current);
  }, [form, isNew]);

  const handleTitleChange = (val: string) => {
    setForm(prev => ({
      ...prev,
      title: val,
      ...(slugManual ? {} : { slug: slugify(val) }),
    }));
  };

  const buildPayload = () => {
    const amountKr = parseInt(form.prepayment_amount_default_kr, 10);
    return {
      ...(existing?.id ? { id: existing.id } : {}),
      producer_id: farmId!,
      title: form.title.trim(),
      slug: form.slug.trim(),
      description_short: form.description_short.trim() || null,
      description: form.description.trim() || null,
      status: form.status,
      beds: form.beds ? parseInt(form.beds) : null,
      guest_capacity: form.guest_capacity ? parseInt(form.guest_capacity) : null,
      price_per_night_text: form.price_per_night_text.trim() || null,
      booking_mode: form.booking_mode,
      booking_deadline_hours: parseInt(form.booking_deadline_hours) || 24,
      check_in_time: form.check_in_time || null,
      check_out_time: form.check_out_time || null,
      house_rules: form.house_rules.trim() || null,
      prepayment_policy_default: form.prepayment_policy_default,
      prepayment_type: form.prepayment_type,
      prepayment_percentage: form.prepayment_type === 'percentage'
        ? Math.max(0, Math.min(100, parseInt(form.prepayment_percentage, 10) || 60))
        : 60,
      prepayment_amount_default_ore: form.prepayment_type === 'fixed' && form.prepayment_policy_default !== 'none' && amountKr > 0
        ? amountKr * 100
        : null,
    } as any;
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Ange en titel');
      return;
    }
    if (!form.slug.trim()) {
      toast.error('Slug saknas');
      return;
    }

    try {
      await upsert.mutateAsync(buildPayload());
      toast.success(isNew ? 'Boende skapat!' : 'Boende uppdaterat!');
      initialSnapshot.current = { ...form };
      if (isNew) navigate(-1);
    } catch (err: any) {
      toast.error(err?.message || 'Kunde inte spara');
    }
  };

  const handleAddBlock = async () => {
    if (!blockRange?.from || !blockRange?.to || !accommodationId) return;
    if (blockRange.from >= blockRange.to) {
      toast.error('Slutdatum måste vara efter startdatum');
      return;
    }
    try {
      await addBlock.mutateAsync({
        accommodation_id: accommodationId,
        start_date: format(blockRange.from, 'yyyy-MM-dd'),
        end_date: format(blockRange.to, 'yyyy-MM-dd'),
        note: blockNote.trim() || undefined,
      });
      setBlockRange(undefined);
      setBlockNote('');
      toast.success('Blockerade datum tillagda');
    } catch (err: any) {
      toast.error(err?.message || 'Kunde inte lägga till blockering');
    }
  };

  // ── Blocking mode: build booked date set ──
  const today = useMemo(() => startOfDay(new Date()), []);

  const unavailableDateSet = useMemo(() => {
    const set = new Set<string>();
    if (bookings) {
      for (const b of bookings) {
        if (b.status === 'cancelled') continue;
        let current = new Date(b.check_in + 'T00:00:00');
        const end = new Date(b.check_out + 'T00:00:00');
        while (isBefore(current, end)) {
          set.add(format(current, 'yyyy-MM-dd'));
          current = addDays(current, 1);
        }
      }
    }
    if (blocks) {
      for (const bl of blocks) {
        let current = new Date(bl.start_date + 'T00:00:00');
        const end = new Date(bl.end_date + 'T00:00:00');
        while (isBefore(current, end)) {
          set.add(format(current, 'yyyy-MM-dd'));
          current = addDays(current, 1);
        }
      }
    }
    return set;
  }, [bookings, blocks]);

  const isDateUnavailable = useCallback((date: Date): boolean => {
    return unavailableDateSet.has(format(date, 'yyyy-MM-dd'));
  }, [unavailableDateSet]);

  const disabledForBlocking = useCallback((date: Date) => {
    if (isBefore(date, today)) return true;
    return isDateUnavailable(date);
  }, [today, isDateUnavailable]);

  const handleBlockRangeSelect = useCallback((range: DateRange | undefined) => {
    if (!range?.from) {
      setBlockRange(undefined);
      return;
    }
    if (range.from && range.to) {
      let d = addDays(range.from, 1);
      while (isBefore(d, range.to) || isSameDay(d, range.to)) {
        if (isDateUnavailable(d)) {
          setBlockRange({ from: range.to, to: undefined });
          return;
        }
        d = addDays(d, 1);
      }
    }
    setBlockRange(range);
    if (range.from && range.to) {
      setTimeout(() => setBlockCalendarOpen(false), 200);
    }
  }, [isDateUnavailable]);

  const [isPublishing, setIsPublishing] = useState(false);

  if (!isNew && isLoading) return <p className="text-muted-foreground p-6">Laddar...</p>;

  const activeBookings = bookings?.filter(b => b.status !== 'cancelled') || [];
  const priceLabel = formatPriceLabel(form.price_per_night_text);
  const isPublished = form.status === 'public';

  const handlePublishToggle = async () => {
    if (isNew) return;
    const newStatus = isPublished ? 'draft' : 'public';
    const action = newStatus === 'public' ? 'publicera' : 'avpublicera';

    setIsPublishing(true);
    try {
      await upsert.mutateAsync({
        ...buildPayload(),
        status: newStatus,
      });

      const updated = { ...form, status: newStatus };
      setForm(updated);
      initialSnapshot.current = updated;
      toast.success(newStatus === 'public' ? 'Boendet är nu publicerat' : 'Boendet är nu ett utkast');
    } catch (err: any) {
      toast.error(err?.message || `Kunde inte ${action}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const publicUrl = !isNew && accommodationId ? `/accommodation/${accommodationId}` : null;

  /* ── Summaries ── */
  const basicSummary = getBasicInfoSummary({
    title: form.title,
    beds: form.beds,
    guestCapacity: form.guest_capacity,
    priceText: form.price_per_night_text,
  });

  const bookingSummary = getBookingSettingsSummary({
    bookingMode: form.booking_mode,
    deadlineHours: form.booking_deadline_hours,
    checkIn: form.check_in_time,
    checkOut: form.check_out_time,
  });

  const paymentSummary = getPrepaymentSettingsSummary({
    policy: form.prepayment_policy_default,
    type: form.prepayment_type,
    percentage: form.prepayment_percentage,
    fixedKr: form.prepayment_amount_default_kr,
  });

  const guestSummary = getGuestInfoSummary({
    houseRules: form.house_rules,
    descriptionShort: form.description_short,
  });

  const blockCount = blocks?.length ?? 0;
  const availSummary = getAvailabilitySummary(blockCount);

  return (
    <div className="pb-32 lg:pb-12">
      {/* ── Title input — prominent, editorial ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 pb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline mb-3"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Tillbaka</span>
        </button>
        <Input
          value={form.title}
          onChange={e => handleTitleChange(e.target.value)}
          placeholder="Vad ska boendet heta?"
          className="text-xl font-semibold h-14 border-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
        />
      </div>

      {/* ── Two-column layout: preview-left, controls-right ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-6">
        <EditorLayout
          main={
            <AccommodationPagePreview
              title={form.title}
              descriptionShort={form.description_short}
              description={form.description}
              priceLabel={priceLabel}
              beds={form.beds}
              guestCapacity={form.guest_capacity}
              checkInTime={form.check_in_time}
              checkOutTime={form.check_out_time}
              houseRules={form.house_rules}
              heroImages={heroImages}
              galleryThumbs={galleryStripThumbs}
              heroIndex={heroIndex}
              onHeroIndexChange={setHeroIndex}
            />
          }
          sidebar={
            <div className="space-y-6">
              {/* ── Actions (canonical EditorActionCard) ── */}
              <EditorActionCard
                isPublished={isPublished}
                onPublish={handlePublishToggle}
                onUnpublish={handlePublishToggle}
                publishDisabled={isPublishing || isNew}
                onSave={handleSave}
                isSaving={upsert.isPending}
                saveDisabled={upsert.isPending || !isDirty}
                viewHref={publicUrl || undefined}
              />

              {/* ── Images ── */}
              <EditorSidebarSection heading="Bilder">
                <EditorSidebarCard>
                  <MediaImageManager
                    ownerType="accommodation"
                    ownerId={accommodationId}
                    producerId={farmId}
                    draftSessionId={draftSessionId}
                    coverUpload={coverUpload}
                    galleryUpload={galleryUpload}
                    helperText="Ladda upp omslagsbild och galleri för boendet."
                  />
                </EditorSidebarCard>
              </EditorSidebarSection>

              {/* ── Settings ── */}
              <EditorSidebarSection heading="Inställningar">
                <EditorSectionCard>
                  {/* ── Grunduppgifter ── */}
                  <EditorSectionItem title="Grunduppgifter" icon={FileText} hint={basicSummary} defaultOpen={isNew}>
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="slug">Webbadress</Label>
                        <Input id="slug" value={form.slug} onChange={e => { setSlugManual(true); setForm(prev => ({ ...prev, slug: e.target.value })); }} className="mt-1.5" />
                      </div>
                      <div>
                        <Label htmlFor="desc_short">Kort beskrivning</Label>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">En kort mening som fångar känslan</p>
                        <Textarea id="desc_short" value={form.description_short} onChange={e => setForm(prev => ({ ...prev, description_short: e.target.value }))} rows={2} placeholder="En mening som beskriver boendet" />
                      </div>
                      <div>
                        <Label htmlFor="desc">Beskrivning</Label>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Beskriv boendet, omgivningen och vad som ingår</p>
                        <RichTextEditor
                          value={form.description}
                          onChange={(html) => setForm(prev => ({ ...prev, description: html }))}
                          placeholder="Berätta mer om boendet…"
                          preset="simple"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="beds">Antal bäddar</Label>
                          <Input id="beds" type="number" min={1} value={form.beds} onChange={e => setForm(prev => ({ ...prev, beds: e.target.value }))} className="mt-1.5" />
                        </div>
                        <div>
                          <Label htmlFor="capacity">Max antal gäster</Label>
                          <Input id="capacity" type="number" min={1} value={form.guest_capacity} onChange={e => setForm(prev => ({ ...prev, guest_capacity: e.target.value }))} className="mt-1.5" />
                        </div>
                      </div>

                      {!isNew && accommodationId ? (
                        <AccommodationPricingSection
                          accommodationId={accommodationId}
                          basePriceText={form.price_per_night_text}
                          onBasePriceChange={v => setForm(prev => ({ ...prev, price_per_night_text: v }))}
                        />
                      ) : (
                        <div>
                          <Label htmlFor="price">Baspris per natt</Label>
                          <Input id="price" value={form.price_per_night_text} onChange={e => setForm(prev => ({ ...prev, price_per_night_text: e.target.value }))} placeholder="t.ex. 800" className="mt-1.5" />
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {priceLabel ? `Visas som: "${priceLabel}"` : 'Ange pris i kronor. Visas som "Från X kr per natt".'}
                          </p>
                        </div>
                      )}
                    </div>
                  </EditorSectionItem>

                  {/* ── Bokningar ── */}
                  <EditorSectionItem title="Bokningar" icon={CalendarIcon} hint={bookingSummary} defaultOpen={isNew}>
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="checkin">Incheckning</Label>
                          <Input id="checkin" value={form.check_in_time} onChange={e => setForm(prev => ({ ...prev, check_in_time: e.target.value }))} placeholder="15:00" className="mt-1.5" />
                        </div>
                        <div>
                          <Label htmlFor="checkout">Utcheckning</Label>
                          <Input id="checkout" value={form.check_out_time} onChange={e => setForm(prev => ({ ...prev, check_out_time: e.target.value }))} placeholder="11:00" className="mt-1.5" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="deadline">Bokningsdeadline (timmar innan incheckning)</Label>
                        <Input id="deadline" type="number" min={0} value={form.booking_deadline_hours} onChange={e => setForm(prev => ({ ...prev, booking_deadline_hours: e.target.value }))} className="mt-1.5" />
                      </div>
                      <div>
                        <Label>Bokningsläge</Label>
                        <Select value={form.booking_mode} onValueChange={v => setForm(prev => ({ ...prev, booking_mode: v }))}>
                          <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="required">Bokning krävs</SelectItem>
                            <SelectItem value="none">Bokning avstängd</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </EditorSectionItem>

                  {/* ── Betalning ── */}
                  <EditorSectionItem title="Betalning" icon={CreditCard} hint={paymentSummary}>
                    <div className="space-y-5">
                      <p className="text-sm text-muted-foreground">
                        Välj hur du vanligtvis vill hantera nya bokningar. Du kan fortfarande ändra det för en enskild bokning.
                      </p>
                      <div>
                        <Label className="text-sm font-medium">Standardpolicy</Label>
                        <Select
                          value={form.prepayment_policy_default}
                          onValueChange={(v) => setForm(prev => ({ ...prev, prepayment_policy_default: v as PrepaymentPolicy }))}
                        >
                          <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Ingen förskottsbetalning</SelectItem>
                            <SelectItem value="optional">Valfritt vid bekräftelse</SelectItem>
                            <SelectItem value="required">Krävs som standard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {form.prepayment_policy_default !== 'none' && (
                        <>
                          <div>
                            <Label className="text-sm font-medium">Beräkningsmetod</Label>
                            <Select
                              value={form.prepayment_type}
                              onValueChange={(v) => setForm(prev => ({ ...prev, prepayment_type: v as PrepaymentType }))}
                            >
                              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Andel av totalbeloppet (%)</SelectItem>
                                <SelectItem value="fixed">Fast belopp (kr)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {form.prepayment_type === 'percentage' ? (
                            <div>
                              <Label htmlFor="prepayment-pct">Andel av totalbeloppet</Label>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Input
                                  id="prepayment-pct"
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={form.prepayment_percentage}
                                  onChange={e => setForm(prev => ({ ...prev, prepayment_percentage: e.target.value }))}
                                  className="max-w-[100px]"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Beräknas automatiskt utifrån den totala vistelsekostnaden
                              </p>
                            </div>
                          ) : (
                            <div>
                              <Label htmlFor="default-prepayment-amount">Fast belopp (kr)</Label>
                              <Input
                                id="default-prepayment-amount"
                                type="number"
                                min={1}
                                value={form.prepayment_amount_default_kr}
                                onChange={e => setForm(prev => ({ ...prev, prepayment_amount_default_kr: e.target.value }))}
                                placeholder="t.ex. 500"
                                className="mt-1.5 max-w-[200px]"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Används som förvalt belopp när du accepterar en bokning
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </EditorSectionItem>

                  {/* ── För gästen ── */}
                  <EditorSectionItem title="För gästen" icon={Home} hint={guestSummary}>
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="rules">Husregler</Label>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Regler gästen bör känna till innan bokning</p>
                        <Textarea id="rules" value={form.house_rules} onChange={e => setForm(prev => ({ ...prev, house_rules: e.target.value }))} rows={4} placeholder="Eventuella husregler" />
                      </div>
                    </div>
                  </EditorSectionItem>

                  {/* ── Tillgänglighet ── */}
                  {!isNew && (
                    <EditorSectionItem title="Tillgänglighet" icon={CalendarOff} hint={availSummary}>
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Markera datum som inte är tillgängliga.</p>
                        <div>
                          <Label>Välj datum att blockera</Label>
                          <Popover open={blockCalendarOpen} onOpenChange={setBlockCalendarOpen}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className={cn("w-full justify-start text-left font-normal mt-1.5", !blockRange?.from && "text-muted-foreground")}>
                                <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
                                {blockRange?.from && blockRange?.to
                                  ? `${format(blockRange.from, 'd MMM', { locale: sv })} – ${format(blockRange.to, 'd MMM yyyy', { locale: sv })}`
                                  : blockRange?.from
                                    ? `${format(blockRange.from, 'd MMM', { locale: sv })} → …`
                                    : 'Välj start- och slutdatum'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start" side="bottom">
                              <Calendar
                                mode="range"
                                selected={blockRange}
                                onSelect={handleBlockRangeSelect}
                                disabled={disabledForBlocking}
                                numberOfMonths={2}
                                fromDate={today}
                                locale={sv}
                                className="p-3 pointer-events-auto"
                              />
                              <p className="text-xs text-muted-foreground px-4 pb-3">
                                Grå dagar är redan bokade eller blockerade
                              </p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <Label>Anteckning (valfritt)</Label>
                          <Input value={blockNote} onChange={e => setBlockNote(e.target.value)} placeholder="t.ex. Underhåll" className="mt-1.5" />
                        </div>
                        <Button variant="secondary" onClick={handleAddBlock} disabled={!blockRange?.from || !blockRange?.to || addBlock.isPending}>
                          <Plus className="h-4 w-4 mr-2" />
                          Lägg till blockering
                        </Button>

                        {blocks && blocks.length > 0 && (
                          <div className="space-y-2 mt-2">
                            {blocks.map(block => (
                              <div key={block.id} className="flex items-center justify-between rounded-md border p-3 text-sm">
                                <div>
                                  <span className="font-medium">{block.start_date} → {block.end_date}</span>
                                  {block.note && <span className="ml-2 text-muted-foreground">({block.note})</span>}
                                  <Badge variant="outline" className="ml-2">{block.block_type === 'booking' ? 'Bokad' : 'Manuell'}</Badge>
                                </div>
                                {block.block_type === 'manual_block' && (
                                  <Button variant="ghost" size="icon" onClick={() => deleteBlock.mutate({ blockId: block.id, accommodationId: accommodationId! })}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </EditorSectionItem>
                  )}
                </EditorSectionCard>
              </EditorSidebarSection>

              {/* ── Active bookings summary (admin-only info) ── */}
              {!isNew && activeBookings.length > 0 && (
                <EditorSidebarSection heading="Bokningar">
                  <EditorSidebarCard>
                    <div className="space-y-2">
                      {activeBookings.slice(0, 5).map(booking => (
                        <div key={booking.id} className="flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <span className="font-medium truncate block">{booking.guest_name || booking.guest_email}</span>
                            <span className="text-xs text-muted-foreground">{booking.check_in} → {booking.check_out}</span>
                          </div>
                          <Badge variant={booking.status === 'confirmed' ? 'default' : 'secondary'} className="shrink-0 ml-2 text-[10px]">
                            {booking.status === 'confirmed' ? 'Bekräftad' : booking.status}
                          </Badge>
                        </div>
                      ))}
                      {activeBookings.length > 5 && (
                        <p className="text-xs text-muted-foreground">+ {activeBookings.length - 5} till</p>
                      )}
                    </div>
                  </EditorSidebarCard>
                </EditorSidebarSection>
              )}
            </div>
          }
        />
      </div>

      {/* ── Mobile sticky action bar ── */}
      <StickyBottomBar mobileOnly>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePublishToggle}
          disabled={isPublishing || isNew}
          className="shrink-0"
        >
          {isPublished ? 'Avpublicera' : 'Publicera'}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={upsert.isPending || !isDirty} className="flex-1">
          <Save className="h-3.5 w-3.5 mr-1.5" />
          {upsert.isPending ? 'Sparar...' : 'Spara'}
        </Button>
      </StickyBottomBar>
    </div>
  );
}
